import React from "react";
import { MONTHS } from "../config";
import { Link } from "react-router-dom";
import { FiShield, FiTrash2, FiLock, FiInfo, FiMail } from "react-icons/fi";

function Privacy() {
    const now = new Date();
    const last_update = MONTHS[now.getMonth()] + ' ' + now.getFullYear();
    return (
        <div className="min-h-screen pt-20 pb-24">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-[#6366f1] via-[#8b5cf6] via-[#a855f7] to-[#ec4899] overflow-hidden rounded-b-3xl border-b-4 border-[#fbbf24]/60 shadow-2xl shadow-[#8b5cf6]/50">
                {/* Cosmic nebula overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#3b82f6]/20 via-transparent to-[#f472b6]/20"></div>
                <div className="absolute inset-0 bg-gradient-to-bl from-[#fbbf24]/10 via-transparent to-[#06b6d4]/10"></div>
                
                {/* Space decorations */}
                <div className="absolute top-4 left-4 text-4xl animate-bounce-joy">🔒</div>
                <div className="absolute top-8 right-8 text-3xl animate-float">🛡️</div>
                <div className="absolute bottom-4 left-8 text-3xl animate-wiggle">🔐</div>
                <div className="absolute bottom-8 right-4 text-4xl animate-bounce-joy" style={{ animationDelay: '1s' }}>✨</div>
                <div className="absolute top-1/2 left-1/4 text-2xl animate-twinkle">💫</div>
                <div className="absolute top-1/3 right-1/4 text-2xl animate-float" style={{ animationDelay: '0.5s' }}>🔑</div>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10 text-center">
                    <div className="inline-block p-4 bg-white/20 rounded-full mb-6 backdrop-blur-sm">
                        <FiShield className="text-white text-5xl drop-shadow-lg" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg animate-bounce-joy bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] bg-clip-text text-transparent">
                        Joyloom Studio Privacy Policy
                    </h1>
                    <div className="w-20 h-1 bg-gradient-to-r from-[#fbbf24] via-[#ec4899] to-[#3b82f6] mb-4 mx-auto rounded-full shadow-lg shadow-[#8b5cf6]/60"></div>
                    <p className="text-white/95 text-lg">Last updated: {last_update}</p>
                </div>
            </div>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                {/* Introduction Section */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 drop-shadow-lg flex items-center gap-3">
                            <span className="text-4xl animate-bounce-joy">🔐</span>
                            Our Pledge to Your Privacy
                        </h2>
                        <div className="space-y-4 text-white/90 text-lg leading-relaxed">
                            <p className="text-justify">
                                At Joyloom Studio, we recognize the fundamental importance of your privacy. This comprehensive 
                                document details our practices concerning the acquisition, utilization, safeguarding, and storage 
                                of your personal details during your engagement with our games and associated offerings (collectively, "the Services").
                            </p>
                            <p className="text-justify">
                                Your decision to use the Services signifies your acceptance of the terms outlined in this Data Protection 
                                Statement. We strive for total clarity and are committed to upholding your data privacy entitlements.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Information We Collect Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Information We Gather</h2>
                        <span className="text-3xl animate-bounce-joy">📊</span>
                    </div>
                    <div className="bg-gradient-to-br from-[#6366f1]/30 via-[#8b5cf6]/20 to-[#ec4899]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#fbbf24]/40 shadow-xl">
                        <p className="text-white/95 text-lg leading-relaxed mb-6">
                            The information we collect is essential for delivering, refining, and maintaining the quality of our Services. 
                            We categorize this data as follows:
                        </p>

                        {/* Subsection 2.1 */}
                        <div className="mb-8">
                            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                                <span className="text-2xl">📝</span>
                                2.1 Information You Voluntarily Supply
                            </h3>
                            <p className="text-white/90 mb-4 leading-relaxed">
                                This data is provided by you directly, such as when you register, make a purchase, or seek assistance.
                            </p>
                            <ul className="space-y-3 text-white/90 ml-6">
                                <li className="flex items-start gap-3">
                                    <span className="text-white mt-1">•</span>
                                    <span><span className="font-semibold text-white">Registration Details:</span> This includes your full name, chosen username, email address, user ID, and confidential access credentials.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-white mt-1">•</span>
                                    <span><span className="font-semibold text-white">Correspondence Logs:</span> Records generated when you communicate with our support team or participate in any studio-hosted activities.</span>
                                </li>
                            </ul>
                        </div>

                        {/* Subsection 2.2 */}
                        <div className="mb-8">
                            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                                <span className="text-2xl">⚙️</span>
                                2.2 Data Collected Upon Interaction
                            </h3>
                            <p className="text-white/90 mb-4 leading-relaxed">
                                When you access or use our Services, certain technical and usage details are automatically logged.
                            </p>
                            <ul className="space-y-3 text-white/90 ml-6">
                                <li className="flex items-start gap-3">
                                    <span className="text-white mt-1">•</span>
                                    <span><span className="font-semibold text-white">Technical Identifiers:</span> Unique Device IDs, IP addresses, hardware specifications, operating system version, and browser type.</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="text-white mt-1">•</span>
                                    <span><span className="font-semibold text-white">Gaming Metrics (Performance Data):</span> Data quantifying your interaction with our games, such as total time played, progress through levels, items purchased, and accumulated scores. This data aids in performance diagnostics and user experience optimization.</span>
                                </li>
                            </ul>
                        </div>

                        {/* Subsection 2.3 */}
                        <div>
                            <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                                <span className="text-2xl">🔗</span>
                                2.3 Information from Integrated Platforms
                            </h3>
                            <p className="text-white/90 leading-relaxed ml-6">
                                If you link your Joyloom profile with external services (e.g., Apple Game Center, Google Play Games, 
                                or social media platforms), we may receive profile information based strictly on the privacy preferences 
                                you have configured on those services.
                            </p>
                        </div>
                    </div>
                </section>

                {/* How We Process Information Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">How We Process Your Information</h2>
                        <span className="text-3xl animate-bounce-joy">⚡</span>
                    </div>
                    <div className="bg-gradient-to-br from-[#3b82f6]/30 via-[#6366f1]/20 to-[#8b5cf6]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#3b82f6]/40 shadow-xl">
                        <p className="text-white/95 text-lg leading-relaxed mb-6">
                            We process the collected data strictly for these core operational purposes:
                        </p>
                        <ul className="space-y-4 text-white/90 ml-6">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">🎮</span>
                                <span><span className="font-semibold text-white">Service Delivery:</span> To facilitate gameplay, administer your user account, and ensure all advertised features are fully functional.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">🚀</span>
                                <span><span className="font-semibold text-white">Product Enhancement:</span> To conduct sophisticated analysis of usage trends, resolve technical glitches, and consistently upgrade the performance and feature set of our games.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">💬</span>
                                <span><span className="font-semibold text-white">Customer Relations:</span> To provide timely and effective responses to all inquiries, requests, and support tickets you submit.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">🛡️</span>
                                <span><span className="font-semibold text-white">Security & Legal Compliance:</span> To protect against fraudulent activity, enforce the stipulations of our Terms of Service, and fulfill all mandated legal requirements or official government requests.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">✨</span>
                                <span><span className="font-semibold text-white">Experience Customization:</span> To optimize and personalize the in-game environment according to your usage patterns and demonstrated preferences.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Limited Disclosure Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Limited Disclosure of Information</h2>
                        <span className="text-3xl animate-bounce-joy">🔒</span>
                    </div>
                    <div className="bg-gradient-to-br from-[#ec4899]/30 via-[#f472b6]/20 to-[#fb7185]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#ec4899]/40 shadow-xl">
                        <p className="text-white/95 text-lg leading-relaxed mb-6">
                            Joyloom Studio will never sell, lease, or commercially trade your personal information for purposes such as 
                            behavioral or targeted advertising.
                        </p>
                        <p className="text-white/95 text-lg font-semibold mb-4">
                            We only transfer your information in limited, strictly defined scenarios:
                        </p>
                        <ul className="space-y-4 text-white/90 ml-6">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">⚖️</span>
                                <span><span className="font-semibold text-white">Compliance Obligations:</span> Where disclosure is necessitated by a judicial order, statutory obligation, or where we deem it essential to protect our assets, or the safety of our users or the public.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">🤝</span>
                                <span><span className="font-semibold text-white">Operational Partners:</span> With authorized third-party vendors who execute critical functions for us (e.g., data storage, analytics, transaction processing). These vendors are bound by stringent confidentiality agreements regarding your data.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Data Protection and Retention Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Data Protection and Retention Standards</h2>
                        <span className="text-3xl animate-float">🛡️</span>
                    </div>
                    
                    {/* Data Security */}
                    <div className="mb-6 bg-gradient-to-br from-[#fbbf24]/30 via-[#f59e0b]/20 to-[#d97706]/30 backdrop-blur-sm rounded-xl p-6 border-2 border-[#fbbf24]/40 shadow-xl">
                        <div className="flex items-center gap-3 mb-4">
                            <FiShield className="text-white text-3xl" />
                            <h3 className="text-2xl font-bold text-white">Safeguarding Your Data</h3>
                        </div>
                        <p className="text-white/90 leading-relaxed ml-12">
                            We utilize robust technical and governance protocols (such as multi-factor authentication and encryption) to 
                            shield your data from unauthorized access, accidental loss, or unauthorized modification. Please remember to 
                            maintain the security of your own account credentials.
                        </p>
                    </div>

                    {/* Data Retention */}
                    <div className="bg-gradient-to-br from-[#10b981]/30 via-[#059669]/20 to-[#047857]/30 backdrop-blur-sm rounded-xl p-6 border-2 border-[#10b981]/40 shadow-xl">
                        <div className="flex items-center gap-3 mb-4">
                            <FiTrash2 className="text-white text-3xl" />
                            <h3 className="text-2xl font-bold text-white">Retention and Account Closure</h3>
                        </div>
                        <p className="text-white/90 mb-4 leading-relaxed ml-12">
                            Your personal data is retained for the duration your account is active or for the period necessary to complete 
                            the stated objectives of this policy.
                        </p>
                        <ul className="space-y-3 text-white/90 ml-12">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span><span className="font-semibold text-white">Deactivating Your Account:</span> You have the liberty to terminate your account via the game settings or by contacting our support team directly.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span><span className="font-semibold text-white">Post-Deletion Backup:</span> Certain elements of your data may be retained on secured backup systems for a period of up to <span className="font-semibold">six (6) months</span> following deletion, solely for the purposes of statutory compliance and system integrity before definitive erasure.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Your Rights Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Your Rights Over Your Data</h2>
                        <span className="text-3xl animate-bounce-joy">👤</span>
                    </div>
                    <div className="bg-gradient-to-br from-[#a855f7]/30 via-[#9333ea]/20 to-[#7c3aed]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#a855f7]/40 shadow-xl">
                        <p className="text-white/95 text-lg leading-relaxed mb-6">
                            As a user of Joyloom's Services, you are entitled to exercise the following control over your personal information:
                        </p>
                        <ul className="space-y-4 text-white/90 ml-6">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">📋</span>
                                <span><span className="font-semibold text-white">Review, Modification, or Removal:</span> You maintain the right to ask for access, correction, or permanent deletion of the personal data we hold about you.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1 text-xl">🚪</span>
                                <span><span className="font-semibold text-white">Account Termination:</span> The option to permanently close your account, as detailed in the preceding Section 5, is always available to you.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Policy Updates Section */}
                <section className="mb-16">
                    <div className="flex items-center gap-3 mb-6">
                        <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">Policy Updates and Amendments</h2>
                        <span className="text-3xl animate-float">📝</span>
                    </div>
                    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
                        <p className="text-white/90 text-lg leading-relaxed">
                            This policy may be revised periodically to incorporate changes in our operational procedures or to align with 
                            new legal requirements. We commit to notifying you of any material changes and will always display the "Last Updated" 
                            date at the document's outset. Your continued utilization of our Services subsequent to a policy revision implies 
                            your full acceptance of the updated terms.
                        </p>
                    </div>
                </section>

                {/* Contact Section */}
                <section>
                    <div className="bg-gradient-to-br from-[#6366f1]/30 via-[#8b5cf6]/20 to-[#ec4899]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#fbbf24]/40 shadow-xl text-center">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <FiInfo className="text-white text-3xl" />
                            <h3 className="text-2xl font-bold text-white">Questions About Our Privacy Policy?</h3>
                        </div>
                        <p className="text-white/90 mb-6 leading-relaxed max-w-2xl mx-auto">
                            If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                            please don't hesitate to reach out to us.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-[#fef3c7] rounded-xl transition text-[#6366f1] font-bold shadow-lg shadow-white/30 joy-hover"
                        >
                            <FiMail className="text-xl" />
                            Contact Us
                        </Link>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default Privacy;
